import { ArrowLeft, ArrowRight, Ellipsis } from 'lucide-react'
import { Button } from './button'
import { useEffect, useState } from 'react'

type PaginationProps = {
    current: number
    totalPages?: number
    onChange: (page: number) => void
    size?: 'Desktop' | 'Mobile'
}

type PaginationItem = number | 'ellipsis'

const MOBILE_BREAKPOINT = 768
const MOBILE_WINDOW_RADIUS = 1
const DESKTOP_WINDOW_RADIUS = 2
const MOBILE_UNKNOWN_THRESHOLD = 3
const DESKTOP_UNKNOWN_THRESHOLD = 5

function buildRange(start: number, end: number): number[] {
    if (end < start) return []
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

function buildUnknownTotalItems(current: number, windowRadius: number, threshold: number): PaginationItem[] {
    if (current <= threshold) {
        return [...buildRange(1, current), 'ellipsis']
    }

    const start = Math.max(2, current - windowRadius)
    const items: PaginationItem[] = [1]

    if (start > 2) items.push('ellipsis')
    items.push(...buildRange(start, current), 'ellipsis')

    return items
}

function buildKnownTotalItems(current: number, totalPages: number, windowRadius: number): PaginationItem[] {
    if (totalPages <= 5) return buildRange(1, totalPages)

    const start = Math.max(2, current - windowRadius)
    const end = Math.min(totalPages - 1, current + windowRadius)
    const items: PaginationItem[] = [1]

    if (start > 2) items.push('ellipsis')
    items.push(...buildRange(start, end))
    if (end < totalPages - 1) items.push('ellipsis')

    items.push(totalPages)
    return items
}

function buildPaginationItems(current: number, totalPages: number | undefined, isMobile: boolean): PaginationItem[] {
    const windowRadius = isMobile ? MOBILE_WINDOW_RADIUS : DESKTOP_WINDOW_RADIUS

    if (totalPages === undefined) {
        const threshold = isMobile ? MOBILE_UNKNOWN_THRESHOLD : DESKTOP_UNKNOWN_THRESHOLD
        return buildUnknownTotalItems(current, windowRadius, threshold)
    }

    return buildKnownTotalItems(current, totalPages, windowRadius)
}

function PageButton({ page, selected, onClick }: { page: number; selected?: boolean; onClick: (p: number) => void }) {
    if (selected) {
        return (
            <div
                aria-current="page"
                className={
                    'w-9 h-9 px-4 py-2 bg-white rounded-[100px] outline outline-1 outline-offset-[-1px] outline-neutral-300 flex justify-center items-center gap-1'
                }
            >
                <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">{page}</div>
            </div>
        )
    }

    return (
        <Button variant="ghost" onClick={() => onClick(page)} className="w-9 h-9 px-4 py-2">
            <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">{page}</div>
        </Button>
    )
}

export default function Pagination({ current, totalPages, onChange, size }: PaginationProps) {
    const [isMobile, setIsMobile] = useState<boolean>(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    if (totalPages !== undefined && totalPages <= 1) return null

    const go = (p: number) => {
        if (p < 1 || p === current) return
        if (totalPages !== undefined && p > totalPages) return
        onChange(p)
    }

    const effectiveIsMobile = size ? size === 'Mobile' : isMobile
    const pages = buildPaginationItems(current, totalPages, effectiveIsMobile)

    return (
        <div
            data-number-of-pages={totalPages}
            data-selected={current}
            data-size={effectiveIsMobile ? 'Mobile' : 'Desktop'}
            className="inline-flex justify-start items-center gap-2"
        >
            <Button
                onClick={() => go(current - 1)}
                className="px-3"
                disabled={current === 1}
                aria-disabled={current === 1}
                variant="ghost"
            >
                <ArrowLeft size={16} />
                <div className="hidden md:block justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                    Previous
                </div>
            </Button>

            {pages.map((p, idx) =>
                p === 'ellipsis' ? (
                    <div
                        key={`e-${idx}`}
                        className="p-1.5 bg-white rounded-lg inline-flex justify-center items-center gap-1"
                    >
                        <Ellipsis />
                    </div>
                ) : (
                    <PageButton key={p} page={p as number} selected={p === current} onClick={go} />
                ),
            )}

            <Button
                variant="ghost"
                className="px-3"
                onClick={() => go(current + 1)}
                disabled={totalPages !== undefined ? current === totalPages : false}
                aria-disabled={totalPages !== undefined ? current === totalPages : false}
            >
                <div className="hidden md:block justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                    Next
                </div>
                <ArrowRight size={16} />
            </Button>
        </div>
    )
}
